import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    ScrollView,
    Keyboard
} from 'react-native-web';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { useRoute } from '@react-navigation/native';

// Custom Components
import LoadingView from '../../components/component_loading_view';
import SectionContainer from '../ProfileEdit/component_section_container';
import { CityPickerModal, NavBar, TextBox } from '../../components';

// Utils
import {
    addThousandsToNumber,
    checkEmail,
    extractNumbersOnly,
    getNumberFromStringWithCommas,
    showAlert,
    showAppError,
    toTitleCase,
    validateUSNumber
} from '../../utils/util_helpers';
import { checkDate } from '../../modules/converters';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

// APIs
import { createExternalCateringLead, updateExternalCateringLead } from '../../modules/api_catering';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';

export function CateringExternalLead(props, ref) {
    const OCCASION_PRESET = ['Birthday', 'Wedding', 'Corporate'];
    const EVENT_TYPE_PRESET = ['Food Truck Service', 'Buffet', 'Drop Off'];

    // region Props
    const { myLeadData, onMyLeadUpdated, onLeadCreated } = useRoute()?.params || {};
    // endregion

    // region Refs
    const txtName = useRef();
    const txtCompanyName = useRef();
    const txtEmail = useRef();
    const txtPhone = useRef();
    const txtAddress = useRef();
    const txtAttendees = useRef();
    const txtBudgetMin = useRef();
    const txtBudgetMax = useRef();
    const txtClientNote = useRef();
    const txtVendorNote = useRef();
    const cityPicker = useRef();
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(false);

    const [name, setName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    const [address, setAddress] = useState('');
    const [cityID, setCityID] = useState(null);

    const [startTime, setStartTime] = useState(moment().add(6, 'hour').unix());
    const [showStartDatePicker, setShowStartDatePicker] = useState(false);
    const [showStartTimePicker, setShowStartTimePicker] = useState(false);
    const [showEndDatePicker, setShowEndDatePicker] = useState(false);
    const [endTime, setEndTime] = useState(moment().add(7, 'hour').unix());
    const [showEndTimePicker, setShowEndTimePicker] = useState(false);

    const [attendees, setAttendees] = useState('');
    const [budgetMin, setBudgetMin] = useState('');
    const [budgetMax, setBudgetMax] = useState('');

    const [clientNote, setClientNote] = useState('');
    const [vendorNote, setVendorNote] = useState('');

    const [paidByHost, setPaidByHost] = useState(true);
    const [occasion, setOccasion] = useState(OCCASION_PRESET[0]);
    const [eventTypeID, setEventTypeID] = useState(1);
    // endregion

    // region Derived Data
    const sendingAttendees = useMemo(() => getNumberFromStringWithCommas(attendees), [attendees]);
    const sendingMin = useMemo(() => getNumberFromStringWithCommas(budgetMin || '0'), [budgetMin]);
    const sendingMax = useMemo(() => getNumberFromStringWithCommas(budgetMax), [budgetMax]);
    // endregion

    // region Function - Preload Values
    const preLoadData = () => {
        if (!myLeadData) return;

        setName(myLeadData.name || '');
        setCompanyName(myLeadData['company_name'] || '');
        setEmail(myLeadData.email || '');
        setPhone(myLeadData.phone || '');
        setAddress(myLeadData.address || '');
        setCityID(myLeadData.city);
        setStartTime(myLeadData.timeObj?.startTime);
        setEndTime(myLeadData.timeObj?.endTime);
        setAttendees(myLeadData['participants'] ? addThousandsToNumber(myLeadData['participants']) : '');
        setBudgetMin(myLeadData['budget'] ? addThousandsToNumber(myLeadData['budget']) : '');
        setBudgetMax(myLeadData['max_budget'] ? addThousandsToNumber(myLeadData['max_budget']) : '');
        setClientNote(myLeadData['comment'] || '');
        setVendorNote(myLeadData['vendor_note'] || '');
        setPaidByHost(myLeadData['paying'] === 1);
        setOccasion(myLeadData['occasion'] || '');
        setEventTypeID(myLeadData['kind'] || 1);
    };

    const resetData = () => {
        setName('');
        setCompanyName('');
        setEmail('');
        setPhone('');
        setAddress('');
        setCityID(null);
        setStartTime(moment().add(6, 'hour').unix());
        setEndTime(moment().add(7, 'hour').unix());
        setAttendees('');
        setBudgetMin('');
        setBudgetMax('');
        setClientNote('');
        setVendorNote('');
        setPaidByHost(true);
        setOccasion(OCCASION_PRESET[0]);
        setEventTypeID(1);
    };

    const hasChanges = () => {
        if (!myLeadData) return false;
        const oldPaidByHost = (myLeadData['paying'] === 1);
        return(
            name.trim().localeCompare(myLeadData['name']?.trim()) !== 0 ||
            companyName.trim().localeCompare((myLeadData['company_name'] || '').trim()) !== 0 ||
            email.trim().localeCompare((myLeadData['email'] || '').trim()) !== 0 ||
            extractNumbersOnly(phone || '').localeCompare(extractNumbersOnly(myLeadData['phone'] || '')) !== 0 ||
            address.trim().localeCompare((myLeadData['address'] || '').trim()) !== 0 ||
            cityID !== myLeadData.city ||
            startTime !== myLeadData.timeObj?.startTime ||
            endTime !== myLeadData.timeObj?.endTime ||
            getNumberFromStringWithCommas(attendees) !== myLeadData['participants'] ||
            sendingMin !== getNumberFromStringWithCommas(myLeadData['budget']) ||
            sendingMax !== getNumberFromStringWithCommas(myLeadData['max_budget']) ||
            clientNote.trim().localeCompare((myLeadData['comment'] || '').trim()) !== 0 ||
            vendorNote.trim().localeCompare((myLeadData['vendor_note'] || '').trim()) !== 0 ||
            paidByHost !== oldPaidByHost ||
            occasion.trim().localeCompare((myLeadData['occasion'] || '').trim()) !== 0 ||
            eventTypeID !== myLeadData['kind']
        );
    };
    // endregion

    // region Component Mounting
    useEffect(() => {
        preLoadData();
    }, []);
    // endregion

    // region Function - Check Error Input
    const hasInputError = () => {
        // Check for errors
        if (!name || !email || !phone || !address || !cityID || !occasion || !attendees) {
            showAlert({ text: 'Please fill out all required field<br/>(marked with <b style="color: red;">red</b> borders)' });
            return true;
        }

        if (!checkEmail(email)) {
            showAlert({ text: 'Please enter a valid email address' });
            return true;
        }

        if (!validateUSNumber(phone)) {
            showAlert({ text: 'Please enter a valid phone number' });
            return true;
        }

        // Check for attendees
        if (sendingAttendees <= 0) {
            showAlert({ text: 'Number of attendees cannot be less than or equal to zero' });
            return true;
        }

        // Check for budget
        if (sendingMin < 0) {
            showAlert({ text: 'Minimum budget cannot be less than zero' });
            return true;
        }

        if ((sendingMax > 0) && (sendingMin > sendingMax)) {
            showAlert({ text: 'Minimum budget cannot be greater than maximum budget' });
            return true;
        }

        return false;
    };
    // endregion

    // region Function - Generate Sending Data
    const generateSendingData = () => ({
        'food_truck_id': globalTruckData.id,

        'name': name,
        'company_name': companyName?.trim() ? companyName : undefined,
        'phone': extractNumbersOnly(phone),
        'email': email,

        'address': address,
        'city': cityID,

        'start_time': moment.unix(startTime).format('YYYY-MM-DD HH:mm:ss'),
        'end_time': moment.unix(endTime).format('YYYY-MM-DD HH:mm:ss'),

        'participants': sendingAttendees,
        'min_budget': sendingMin,
        'max_budget': sendingMax > 0 ? sendingMax : undefined,

        'consumer_note': clientNote,
        'vendor_note': vendorNote,

        'paying': paidByHost ? 1 : 0,

        'occasion': occasion,

        'kind': Number(eventTypeID)
    });
    // endregion

    // region Function - Go Create Lead
    const goCreateLead = () => {
        if (hasInputError()) return;

        // Start process
        showAlert({
            text: 'Confirm adding this lead to your Truckster Vendor account?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    setLoading(true);

                    // Call API
                    createExternalCateringLead(globalTruckData.id, generateSendingData())
                        .then((newLeadData) => {
                            // Reset values
                            resetData();

                            // Callback
                            onLeadCreated(Object.assign(newLeadData, { timeObj: checkDate(newLeadData, true) }));

                            // Stop loading
                            setLoading(false);

                            // Close modal
                            RootNavigation.pop();

                            // Notify
                            showAlert({ text: 'Catering lead successfully added' });
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setLoading(false));
                }
            }
        })
    };
    // endregion

    // region Function - Update My Lead Data
    const goUpdateMyLeadData = () => {
        if (hasInputError()) return;

        setLoading(true);
        updateExternalCateringLead(globalTruckData.id, Object.assign(generateSendingData(), { id: myLeadData?.id }))
            .then((result) => {
                const newCateringData = Object.assign(cloneDeep(myLeadData), result);
                onMyLeadUpdated(newCateringData);
                RootNavigation.pop();
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoading(false));
    };
    // endregion

    // region Render Title
    const renderTitle = (text) => (
        <Text style={{ fontWeight: '500', fontSize: 18, marginTop: 32, marginBottom: 16 }}>
            {text}
        </Text>
    );
    // endregion

    // region Render Selectable Button
    const renderSelectableButton = (text = '', onPress = () => {}, isSelected = false, fullFlex = true, isMissing = false) => (
        <TouchableOpacity
            key={toTitleCase(text)}
            onPress={onPress}
            style={{
                flex: fullFlex ? 1 : undefined, paddingVertical: 16, paddingHorizontal: fullFlex ? 0 : 20,
                marginRight: 8, alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
                backgroundColor: isSelected ? (isMissing ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BASIC.BLACK_TWO) : 'white',
                borderWidth: 2, borderColor: isSelected ? (isMissing ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BASIC.BLACK_TWO) : GLOBAL.COLOR.DIVIDER
            }}>
            <Text style={{ fontSize: 18, fontWeight: '500', color: isSelected ? 'white' : GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderSelectableButtonGroup = (children) => (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {children}
        </View>
    );
    // endregion

    // region Render Client Information
    const renderClientInfoSection = () => (
        <SectionContainer title={'Client Information'} childContainerStyle={{ marginTop: 12 }}>
            {/* Truck Name */}
            <TextBox
                ref={txtName}
                title={'Name'}
                imageName={'user'}
                useFeather={true}
                imageSize={28}
                placeholder={'Client\'s Full Name'}
                autoCapitalize={'words'}
                keyboardType={'default'}
                value={name}
                onChangeText={setName}
                onSubmitEditing={() => txtCompanyName.current?.focus()}
                showError={!name?.trim()}
            />

            {/* Company Name Input */}
            <TextBox
                ref={txtCompanyName}
                title={'Company Name (Optional)'}
                imageName={'briefcase'}
                useFeather={true}
                imageSize={28}
                placeholder={'Client\'s Company Name (Optional)'}
                autoCapitalize={'words'}
                keyboardType={'default'}
                value={companyName}
                onChangeText={setCompanyName}
                onSubmitEditing={() => txtEmail.current?.focus()}
            />

            {/* Email Input */}
            <TextBox
                ref={txtEmail}
                title={'Email'}
                imageName={'mail'}
                useFeather={true}
                imageSize={28}
                placeholder={'Email Address'}
                autoCapitalize={'none'}
                keyboardType={'email-address'}
                value={email}
                onChangeText={setEmail}
                onSubmitEditing={() => txtPhone.current?.focus()}
                showError={!email?.trim() || !checkEmail(email)}
            />

            {/* Phone Input */}
            <TextBox
                ref={txtCompanyName}
                title={'Phone Number'}
                imageName={'phone'}
                useFeather={true}
                imageSize={28}
                placeholder={'Phone Number'}
                autoCapitalize={'words'}
                keyboardType={'number-pad'}
                useMaskedInput={true}
                value={phone}
                onChangeText={setPhone}
                onSubmitEditing={() => txtAddress.current?.focus()}
                showError={!phone?.trim() || !validateUSNumber(phone)}
            />
        </SectionContainer>
    );
    // endregion

    // region Render Catering Address
    const renderCateringAddress = () => (
        <>
            {/* Address */}
            <TextBox
                ref={txtAddress}
                title={'Address'}
                imageName={'map-pin'}
                useFeather={true}
                imageSize={28}
                placeholder={'Street number, city, state, and zip code'}
                autoCapitalize={'words'}
                keyboardType={'default'}
                value={address}
                onChangeText={setAddress}
                onSubmitEditing={() => Keyboard.dismiss()}
                showError={!address?.trim()}
            />

            {/* Nearest Serving City */}
            <TextBox
                title={'Nearest Serving City'}
                imageName={'map'}
                useFeather={true}
                imageSize={28}
                placeholder={'Select the nearest serving city'}
                value={GLOBAL.CITIES[cityID] ? `${GLOBAL.CITIES[cityID]?.name}, ${GLOBAL.CITIES[cityID]?.state?.['short_name']}` : ''}
                onPress={() => cityPicker.current?.showModal()}
                showError={!cityID}
            />
        </>
    );
    // endregion

    // region Render Date of Event

    // region Render Start Time Picker
    const renderStartTimePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                visible={showStartDatePicker}
                saveLabelDisabled={true}
                date={moment.unix(startTime).toDate()}
                validRange={{ startDate: moment().startOf('day').toDate() }}
                onChange={({ date }) => {
                    const newDate = moment.unix(startTime).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setStartTime(newDate.unix());
                    setShowStartDatePicker(false);
                    setShowStartTimePicker(true);
                }}
                onDismiss={() => setShowStartDatePicker(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showStartTimePicker}
                hours={moment.unix(startTime).hours()}
                minutes={moment.unix(startTime).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment.unix(startTime).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setStartTime(newDate.unix());
                    setShowStartTimePicker(false);

                    // If start time is after end time
                    if (newDate.isAfter(moment(endTime))) {
                        setEndTime(newDate.clone().add(1, 'hour').unix());
                    }
                }}
                onDismiss={() => setShowStartTimePicker(false)}
            />
        </>
    );
    // endregion

    // region Render End Time Picker
    const renderEndTimePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                visible={showEndDatePicker}
                saveLabelDisabled={true}
                date={moment.unix(endTime).toDate()}
                onChange={({ date }) => {
                    const newDate = moment.unix(endTime).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setEndTime(newDate.unix());
                    setShowEndDatePicker(false);
                    setShowEndTimePicker(true);
                }}
                onDismiss={() => setShowEndDatePicker(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showEndTimePicker}
                hours={moment.unix(endTime).hours()}
                minutes={moment.unix(endTime).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment.unix(endTime).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setEndTime(newDate.unix());
                    setShowEndTimePicker(false);

                    // If end time is before start time
                    if (newDate.isBefore(moment.unix(startTime))) {
                        setStartTime(newDate.clone().subtract(1, 'hour').unix());
                    }
                }}
                onDismiss={() => setShowEndTimePicker(false)}
            />
        </>
    );
    // endregion

    const isMultipleDays = useMemo(() => moment.unix(endTime).isAfter(moment.unix(startTime), 'date'), [startTime, endTime]);
    const renderDateOfEvent = () => (
        <>
            {/* Date of Event Title */}
            {renderTitle('Time of Event')}
            {
                isMultipleDays &&
                <Text style={{ marginVertical: 8, color: GLOBAL.COLOR.BASIC.UI_YELLOW }}>
                    Multi-day event selected
                </Text>
            }

            {/* Start Time Selector */}
            <TextBox
                imageName={'calendar'}
                useFeather={true}
                imageSize={28}
                title={'Start Date/Time'}
                placeholder={'Select Start Date/Time'}
                value={startTime ? moment.unix(startTime).format('MMM D, YYYY | h:mm A') : ''}
                onPress={() => setShowStartDatePicker(true)}
            />

            {/* End Time Selector */}
            <TextBox
                imageName={'calendar'}
                useFeather={true}
                imageSize={28}
                title={'End Date/Time'}
                placeholder={'Select End Date/Time'}
                value={endTime ? moment.unix(endTime).format('MMM D, YYYY | h:mm A') : ''}
                onPress={() => setShowEndDatePicker(true)}
            />

            {/* Date Time Pickers */}
            {renderStartTimePicker()}
            {renderEndTimePicker()}
        </>
    );
    // endregion

    // region Render Budget
    const renderBudget = () => (
        <>
            {/* Budget Title */}
            {renderTitle('Attendees & Budget')}

            {/* Attendees */}
            <TextBox
                ref={txtAttendees}
                title={'Attendees'}
                imageName={'users'}
                useFeather={true}
                imageSize={28}
                placeholder={'Number of Attendees'}
                keyboardType={'number-pad'}
                value={attendees?.toString()}
                textInputStyle={{ color: getNumberFromStringWithCommas(attendees) > 0 ? GLOBAL.COLOR.BASIC.BLACK_TWO : GLOBAL.COLOR.MAIN }}
                onChangeText={(text) => setAttendees(addThousandsToNumber(text))}
                onSubmitEditing={() => txtBudgetMin.current?.focus()}
            />

            {/* Min/Max */}
            <TextBox
                ref={txtBudgetMin}
                title={'Minimum Budget'}
                placeholder={'Minimum Budget'}
                keyboardType={'number-pad'}
                prefix={'$'}
                value={budgetMin?.toString()}
                textInputStyle={{ color: sendingMin > -1 ? GLOBAL.COLOR.BASIC.BLACK_TWO : GLOBAL.COLOR.MAIN }}
                onChangeText={(text) => setBudgetMin(addThousandsToNumber(text))}
                onSubmitEditing={() => txtBudgetMax.current?.focus()}
            />

            <TextBox
                ref={txtBudgetMin}
                title={'Maximum Budget'}
                placeholder={'Maximum Budget'}
                keyboardType={'number-pad'}
                prefix={'$'}
                value={budgetMax?.toString()}
                textInputStyle={{ color: ((sendingMax > 0) && (sendingMin > sendingMax)) ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BASIC.BLACK_TWO }}
                onChangeText={(text) => setBudgetMax(addThousandsToNumber(text))}
                onSubmitEditing={() => txtClientNote.current?.focus()}
            />
        </>
    );
    // endregion

    // region Render Client Notes
    const renderClientNotes = () => (
        <>
            {/* Note Title */}
            {renderTitle('Notes (Optional)')}

            {/* Client Notes */}
            <TextBox
                ref={txtClientNote}
                imageName={'file-text'}
                useFeather={true}
                imageSize={28}
                title={'Client Notes (Optional)'}
                placeholder={'Client Notes (Optional)'}
                multiline={true}
                contentBoxStyle={{ height: 200 }}
                value={clientNote?.toString()}
                onChangeText={setClientNote}
            />

            {/* Vendor Notes */}
            <TextBox
                ref={txtVendorNote}
                imageName={'edit-3'}
                useFeather={true}
                imageSize={28}
                title={'My Notes (Optional)'}
                placeholder={'Enter your notes here (Optional)'}
                multiline={true}
                contentBoxStyle={{ height: 200 }}
                value={vendorNote?.toString()}
                onChangeText={setVendorNote}
            />
        </>
    );
    // endregion

    // region Render Other Details
    const checkOccasionSelected = (text) => (text?.trim().toLowerCase() === occasion?.trim().toLowerCase());
    const isOther = useMemo(() => OCCASION_PRESET.every((text) => !checkOccasionSelected(text)), [occasion]);
    const checkTypeSelected = (id) => (id === eventTypeID);
    const renderOtherDetails = () => (
        <>
            {/* Title */}
            {renderTitle('Paying By')}

            {/* Buttons */}
            {renderSelectableButtonGroup(
                <>
                    {renderSelectableButton('Host', () => setPaidByHost(true), paidByHost)}
                    {renderSelectableButton('Attendees', () => setPaidByHost(false), !paidByHost)}
                </>
            )}

            {/* Title */}
            {renderTitle('Occasion')}

            {/* Buttons */}
            {renderSelectableButtonGroup(
                <>
                    {OCCASION_PRESET.map((text) => renderSelectableButton(text, () => setOccasion(text), checkOccasionSelected(text)))}
                    {renderSelectableButton('Other', () => setOccasion(''), isOther, true, !occasion?.trim())}
                </>
            )}

            {/* Other Input Box */}
            {
                isOther &&
                <TextBox
                    title={'Occasion Name'}
                    placeholder={'Enter occasion here'}
                    value={occasion}
                    onChangeText={setOccasion}
                    autoCapitalize={'words'}
                    containerStyle={{ marginVertical: 16 }}
                    contentBoxStyle={{ borderColor: !occasion?.trim() ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.DIVIDER }}
                    autoCorrect={true}
                />
            }

            {/* Title */}
            {renderTitle('Event Type')}

            {/* Buttons */}
            {renderSelectableButtonGroup(
                <>
                    {EVENT_TYPE_PRESET.map((text, index) => renderSelectableButton(
                        text,
                        () => setEventTypeID(index + 1),
                        checkTypeSelected(index + 1),
                        index === 0
                    ))}
                </>
            )}
        </>
    );
    // endregion

    // region Render Catering Info Section
    const renderCateringInfoSection = () => (
        <SectionContainer title={'Catering Event Details'} childContainerStyle={{ marginTop: 12, paddingBottom: 24 }}>
            {/* Catering Address */}
            {renderCateringAddress()}

            {/* Date of Event */}
            {renderDateOfEvent()}

            {/* Budget */}
            {renderBudget()}

            {/* Client Notes */}
            {renderClientNotes()}

            {/* Paying By */}
            {renderOtherDetails()}

            {/* City Picker */}
            <CityPickerModal
                ref={cityPicker}
                title={'Nearest Serving City'}
                showDescription={true}
                singleSelect={true}
                visibleCityIDs={(globalTruckData?.city || []).map((city) => Number(city.id))}
                onCitySelected={(id) => setCityID(id)}
                selectedCityIDs={cityID ? [cityID] : []}
            />
        </SectionContainer>
    );
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%' }}>
            {/* Nav Bar */}
            <NavBar
                title={`${myLeadData ? 'Edit' : 'Add'} Catering Lead`}
                subTitle={hasChanges() ? 'Unsaved Changes' : ''}
                ignoreTopBarHeight={true}
                leftButtonOnPress={() => {
                    if (hasChanges()) {
                        showAlert({
                            text: 'You have unsaved changes, are you sure you want to discard them?',
                            cancelButton: { text: 'Cancel' },
                            confirmButton: {
                                text: 'Discard',
                                color: GLOBAL.COLOR.MAIN,
                                onPress: () => RootNavigation.pop()
                            }
                        });
                    }
                    else RootNavigation.pop();
                }}
                rightButtonBackgroundColor={GLOBAL.COLOR.MAIN}
                rightButtonColor={'white'}
                rightButtonTitle={isLoading ? '' : (myLeadData ? (hasChanges() ? 'Update' : '') : 'Add')}
                rightButtonOnPress={myLeadData ? goUpdateMyLeadData : goCreateLead}
            />

            {/* Main Content */}
            <ScrollView
                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                contentContainerStyle={{ paddingBottom: 32, paddingHorizontal: '15%' }}>
                {/* Contact Info */}
                {renderClientInfoSection()}

                {/* Catering Info */}
                {renderCateringInfoSection()}
            </ScrollView>

            {/* Loading View */}
            {
                isLoading &&
                <LoadingView
                    isLoading={true}
                    description={`${myLeadData ? 'Updating' : 'Adding'} catering lead`}
                    isAbsolute={true}
                />
            }
        </View>
    );
    // endregion
}
